import { LabelId, UserCommunication } from '@this+that/models';

export enum CommunicationIntentKind {
  LABELS = 'labels',
  SNOOZED = 'snoozed',
  LINKED_TO_ENTRY = 'linkedToEntry',
}

export interface CommunicationIntent {
  messageId: string;
  threadMessageId?: string;
  internetMessageId?: string;
  kind: CommunicationIntentKind;
  addLabelIds?: LabelId[];
  removeLabelIds?: LabelId[];
  createdAt: number;
  // TTL clock, restarted by settleIntent on mutation success; falls back to
  // createdAt for intents hydrated before this field existed.
  expiresFrom?: number;
  // Set when the server first agreed. The intent stops correcting labels from
  // then on but survives as the reinjection anchor, because UserCommunicationsV2
  // and the UserThreadViews GSI the pagination reads converge at different times.
  satisfiedAt?: number;
}

export interface CommunicationIntentIdentity {
  messageId: string;
  threadMessageId?: string;
  internetMessageId?: string;
}

export interface SetLabelsIntentParams extends CommunicationIntentIdentity {
  originalLabelIds: LabelId[] | undefined;
  expectedLabelIds: LabelId[];
}

export interface CommunicationIntentsState {
  intents: Record<string, CommunicationIntent>;
}

export interface ReconcileCommunicationResult {
  communication: UserCommunication;
  // True only when an unsatisfied LABELS intent forced a label correction —
  // i.e. we know the server is stale for this item. Callers that place items
  // by calculateViews (a corrected item routes itself) don't need this; a
  // combined-fetch save that places by the *queried* viewType does, and must
  // gate any calculateViews-based exclusion on it — every uncorrected item
  // (the vast majority) must pass through exactly as the server sent it.
  wasCorrected: boolean;
}

export interface CommunicationIntentsActions {
  setLabelsIntent: (params: SetLabelsIntentParams) => void;
  setSnoozedIntent: (params: CommunicationIntentIdentity) => void;
  setLinkedToEntryIntent: (params: CommunicationIntentIdentity) => void;
  clearIntent: (messageId: string) => void;
  clearIntents: (messageIds: string[]) => void;
  settleIntent: (messageId: string) => void;
  reconcileCommunication: (
    communication: UserCommunication,
  ) => ReconcileCommunicationResult;
  reconcileSnoozedIntents: (snoozedMessageIds: string[]) => void;
  isHiddenByIntent: (messageId: string) => boolean;
  isLinkedByIntent: (messageId: string) => boolean;
  pruneExpiredIntents: () => void;
  resetState: () => void;
}

export type CommunicationIntentsStore = CommunicationIntentsState &
  CommunicationIntentsActions;
